import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import CheckValid from "../../Common/CheckValid";
import { Loading } from "../common/WarrantyStates";
import {
  getDocumentLink,
  getReviewDetail,
  reviewClaim,
  reviewRegistration,
} from "../common/warrantyApi";
import { requestIdFor, settleRequestId } from "../common/clientRequest";
import { formatDate, formatDateTime, formatMinor, safeHttpUrl } from "../common/format";
import {
  dateSourceLabel,
  invoiceCheckStateLabel,
  invoiceFailureLabel,
  registrationStatusLabel,
  reviewReasonLabel,
} from "../common/opsLabels";
import ProductPicker, { useProductOptions } from "./ProductPicker";
import "../warranty.css";

// Codes after which this row is gone or already decided: the queue is read again.
const GONE_CODES = new Set([
  "REGISTRATION_NOT_FOUND",
  "REGISTRATION_NOT_PENDING",
  "CLAIM_NOT_FOUND",
  "CLAIM_ALREADY_RESOLVED",
  "CLAIM_OWNER_CHANGED",
]);
// Codes after which the date moved under the person: the registration is read again.
const MOVED_CODES = new Set([
  "REVIEW_DATE_CHANGED",
  "DATE_DECISION_NOT_APPLICABLE",
  "DATE_DECISION_REQUIRED",
]);

// The purchase date staff read off the invoice (6 Oct 2026). The sources the warranty service
// trusts: replacing a date from one of them needs a reason. Before the floor is refused.
const TRUSTED_DATE_SOURCES = new Set([
  "order_invoice",
  "dealer_entry",
  "invoice_ocr",
  "staff_verified",
  "staff_review",
]);
// "Today" here is the browser's; the service checks the market's, so a date right at midnight can
// still come back as DATE_INVALID, which then shows under the field.
const INVOICE_DATE_FLOOR = "2019-01-01";
const REASON_MAX = 500;
// Refusals about the invoice date, shown under its field rather than in the banner.
const INVOICE_DATE_CODES = new Set(["PURCHASE_DATE_REQUIRED", "DATE_INVALID"]);

const percent = (confidence) =>
  typeof confidence === "number" && Number.isFinite(confidence)
    ? `${Math.round(confidence * 100)}% sure`
    : "";

const DATE_DECISIONS = [
  {
    value: "leave_unverified",
    label: "Leave unverified",
    help: "Approve, but keep the rider's date marked as typed by the rider.",
  },
  {
    value: "confirm",
    label: "Confirm the date",
    help: "The date the rider typed is right. It becomes verified by staff.",
  },
  {
    value: "correct",
    label: "Correct the date",
    help: "The rider typed the wrong date. Enter the right one.",
  },
];

// The sentence on the confirm step: what this decision does, in words.
const effectText = ({
  isClaim,
  choice,
  dateDecision,
  correctDate,
  purchaseDate,
  dateRequired,
  frame,
  invoiceDate,
}) => {
  const invoiceDateText = invoiceDate
    ? purchaseDate
      ? ` The purchase date will change from ${formatDate(purchaseDate)} to ${formatDate(
          invoiceDate
        )}, read off the invoice by staff.`
      : ` The purchase date will be set to ${formatDate(invoiceDate)}, read off the invoice by staff.`
    : "";
  if (isClaim) {
    return choice === "approve"
      ? `The claimant will be recorded as the owner of frame ${frame}.${invoiceDateText}`
      : `The claim is turned down. Frame ${frame} stays registered as it is.`;
  }
  if (choice === "reject") {
    return `Registration ${frame} will be rejected.`;
  }
  if (!dateRequired) {
    return `Registration ${frame} will be approved.${invoiceDateText}`;
  }
  if (dateDecision === "confirm") {
    return `Registration ${frame} will be approved. The purchase date ${formatDate(
      purchaseDate
    )} will be marked as verified by staff.`;
  }
  if (dateDecision === "correct") {
    return `Registration ${frame} will be approved. The purchase date will change from ${formatDate(
      purchaseDate
    )} to ${formatDate(correctDate)} and be marked as verified by staff.`;
  }
  return `Registration ${frame} will be approved. Plans will keep refusing this cycle until its date is verified.`;
};

const DocumentPreview = ({ doc, index, link, onRefresh, onBroken }) => {
  const url = link?.status === "ready" ? safeHttpUrl(link.url) : null;
  const isImage = typeof doc.contentType === "string" && doc.contentType.startsWith("image/");
  const isPdf = doc.contentType === "application/pdf";
  const name = `document ${index + 1}`;
  return (
    <div className="warranty-doc">
      <div className="warranty-doc-head">
        <strong>Document {index + 1}</strong>
        {doc.kind && <span className="warranty-muted"> {doc.kind}</span>}
        {doc.uploadedAt && (
          <span className="warranty-muted"> uploaded {formatDateTime(doc.uploadedAt)}</span>
        )}
      </div>
      <div className="warranty-doc-preview">
        {(!link || link.status === "loading") && <Loading label="Getting the link" />}
        {link?.status === "error" && (
          <div className="warranty-doc-note" role="alert">
            {link.message}{" "}
            <button type="button" className="warranty-inline-btn" onClick={onRefresh}>
              Try again
            </button>
          </div>
        )}
        {link?.status === "ready" && !url && (
          <div className="warranty-doc-note" role="alert">
            The link for this document is not usable.
          </div>
        )}
        {url && isImage && !link.broken && (
          <img
            className="warranty-doc-img"
            src={url}
            alt={`Invoice ${name}`}
            onError={onBroken}
          />
        )}
        {url && isImage && link.broken && (
          <div className="warranty-doc-note" role="alert">
            The preview could not be shown (the link may have expired).{" "}
            <button type="button" className="warranty-inline-btn" onClick={onRefresh}>
              Get a new link
            </button>{" "}
            <a href={url} target="_blank" rel="noopener noreferrer">
              Open {name}
            </a>
          </div>
        )}
        {url && !isImage && (
          <div className="warranty-doc-note">
            <a href={url} target="_blank" rel="noopener noreferrer">
              {isPdf ? "Open invoice" : `Open ${name}`}
            </a>
            <span className="warranty-muted">
              {" "}
              {isPdf ? "(PDF, opens in a new tab)" : "(opens in a new tab)"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const ReviewDecision = ({ item, accessToken, onClose, onDecided, onStale }) => {
  const isClaim = item.reason === "ownership_claim";
  const [detail, setDetail] = useState({
    status: isClaim ? "fallback" : "loading",
    data: null,
    switchedOff: false,
    message: "",
  });
  const [links, setLinks] = useState({});
  const [stage, setStage] = useState("edit");
  const [choice, setChoice] = useState(null);
  const [dateDecision, setDateDecision] = useState("leave_unverified");
  const [correctDate, setCorrectDate] = useState("");
  const [dateError, setDateError] = useState("");
  // The purchase date read off the invoice, for a row whose date is not the rider's own typing.
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceDateError, setInvoiceDateError] = useState("");
  const [invoiceReason, setInvoiceReason] = useState("");
  const [invoiceReasonError, setInvoiceReasonError] = useState("");
  // Set when the service said the row has no date (the field becomes required) or that its date is
  // trusted (a reason is needed), whatever the queue item said; cleared when the row is read again.
  const [dateMissingOverride, setDateMissingOverride] = useState(false);
  const [reasonOverride, setReasonOverride] = useState(false);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [productId, setProductId] = useState("");
  const [productError, setProductError] = useState("");
  // Set once the service has refused the product; it is kept (not read from `failure`, which each
  // new attempt clears) so the picker stays until the registration is reloaded.
  const [productRefused, setProductRefused] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [failure, setFailure] = useState(null);
  // What the OMS last told us about the date's source (REVIEW_DATE_CHANGED sends it; a refusal for
  // a missing or unneeded date decision says which way it is). It wins over the queue item's
  // stale source until the detail is read again, so the decision follows the real state.
  const [sourceOverride, setSourceOverride] = useState(null);
  const mounted = useRef(true);
  const idRef = useRef(null);
  const detailSeq = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadDetail = useCallback(async () => {
    if (isClaim) {
      return;
    }
    const mine = ++detailSeq.current;
    setDetail({ status: "loading", data: null, switchedOff: false, message: "" });
    const result = await getReviewDetail(accessToken, item.registrationId);
    if (!mounted.current || mine !== detailSeq.current) {
      return;
    }
    if (result.ok) {
      setDetail({ status: "ready", data: result.data, switchedOff: false, message: "" });
      setSourceOverride(null);
    } else {
      // Switched off (404) or unreachable: the queue item still carries what a decision needs.
      setDetail({
        status: "fallback",
        data: null,
        switchedOff: result.code === "NOT_FOUND",
        message: result.message,
      });
    }
  }, [accessToken, item.registrationId, isClaim]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const ready = detail.status === "ready" ? detail.data : null;
  // 14 Oct 2026: the date OCR read off the invoice starts the staff date field; staff can change it.
  const ocrPrefillDone = useRef(false);
  useEffect(() => {
    if (ocrPrefillDone.current || !ready) return;
    ocrPrefillDone.current = true;
    const read = (ready.documents || []).find((doc) => doc.ocrDate);
    if (read) setInvoiceDate((current) => (current.trim() === "" ? read.ocrDate : current));
  }, [ready]);
  const purchaseDate = ready ? ready.purchaseDate : item.purchaseDate;
  const source =
    sourceOverride !== null ? sourceOverride : ready ? ready.purchaseDateSource : item.purchaseDateSource;
  const frame = ready ? ready.frameNumber : item.frameNumber;
  const dateRequired =
    !isClaim &&
    (sourceOverride !== null
      ? sourceOverride === "customer_claim"
      : ready
      ? ready.dateDecisionRequired === true
      : source === "customer_claim");
  // The invoice date field: every claim, and a registration whose date is not the rider's own
  // typing (that one has the decision above). Required when the row has no date at all.
  const showInvoiceDate = isClaim || !dateRequired;
  const hasDate = typeof purchaseDate === "string" && purchaseDate !== "";
  const invoiceDateRequired = showInvoiceDate && (!hasDate || dateMissingOverride);
  const reasonNeeded =
    showInvoiceDate &&
    invoiceDate.trim() !== "" &&
    (reasonOverride || (hasDate && TRUSTED_DATE_SOURCES.has(source)));
  const notPending = ready !== null && ready.status !== "pending_review";
  // A frame the OMS has no sale for gives the service no product to resolve, so staff pick one.
  // "No sale record found" always needs it; any other row without a sale product may, and a row
  // the service refused for a missing or unknown product gets the picker whatever the queue said.
  const needsProduct = !isClaim && (!item.sale?.productName || productRefused);
  const productRequired = needsProduct && (item.reason === "no_sale_record" || productRefused);
  const products = useProductOptions(needsProduct);
  const productBlocked = productRequired && products.state === "unavailable";
  const documents = ready
    ? ready.documents
    : (item.documentIds || []).map((id) => ({ id, contentType: null }));
  const docKey = documents.map((doc) => doc.id).join(",");

  const fetchLink = useCallback(
    async (id) => {
      setLinks((prev) => ({ ...prev, [id]: { status: "loading" } }));
      const result = await getDocumentLink(accessToken, id);
      if (!mounted.current) {
        return;
      }
      setLinks((prev) => ({
        ...prev,
        [id]: result.ok
          ? { status: "ready", url: result.data?.url }
          : { status: "error", message: result.message },
      }));
    },
    [accessToken]
  );

  useEffect(() => {
    if (detail.status === "loading") {
      return;
    }
    docKey
      .split(",")
      .filter((id) => id !== "")
      .forEach((id) => fetchLink(id));
  }, [docKey, detail.status, fetchLink]);

  const checkNote = (value, required) => {
    if (!required && value.trim() === "") {
      setNoteError("");
      return true;
    }
    let message = "";
    CheckValid(value.trim(), {
      type: "warranty_text",
      label: "Note",
      max: 500,
      error: (text) => {
        message = text;
      },
    });
    setNoteError(message);
    return message === "";
  };

  const checkDate = (value) => {
    let message = "";
    CheckValid(value, {
      type: "warranty_date",
      min: INVOICE_DATE_FLOOR,
      error: (text) => {
        message = text;
      },
    });
    setDateError(message);
    return message === "";
  };

  const checkInvoiceDate = (value, required) => {
    if (!required && value.trim() === "") {
      setInvoiceDateError("");
      return true;
    }
    let message = "";
    CheckValid(value.trim(), {
      type: "warranty_date",
      min: INVOICE_DATE_FLOOR,
      error: (text) => {
        message = text;
      },
    });
    setInvoiceDateError(message);
    return message === "";
  };

  const checkInvoiceReason = (value) => {
    const text = value.trim();
    const message =
      text === ""
        ? "Say why the verified purchase date is wrong"
        : text.length > REASON_MAX
        ? `The reason can be at most ${REASON_MAX} characters`
        : "";
    setInvoiceReasonError(message);
    return message === "";
  };

  // What the approve sends about the invoice date: nothing on a reject or with no date entered.
  const invoiceDateBody = (decision) => {
    if (decision !== "approve" || !showInvoiceDate || invoiceDate.trim() === "") {
      return {};
    }
    return {
      purchaseDate: invoiceDate.trim(),
      ...(reasonNeeded ? { purchaseDateReason: invoiceReason.trim() } : {}),
    };
  };

  const buildBody = (decision) => {
    const body = { decision };
    if (note.trim() !== "") {
      body.note = note.trim();
    }
    if (decision === "approve" && needsProduct && productId !== "") {
      body.productId = productId;
    }
    if (decision === "approve" && dateRequired) {
      body.dateDecision = dateDecision;
      if (dateDecision === "correct") {
        body.purchaseDate = correctDate.trim();
      }
    }
    return { ...body, ...invoiceDateBody(decision) };
  };

  const goToConfirm = (decision) => {
    setFailure(null);
    const noteOk = checkNote(note, decision === "reject");
    const dateOk = !(decision === "approve" && dateRequired && dateDecision === "correct") || checkDate(correctDate);
    const productOk = !(decision === "approve" && productRequired) || productId !== "";
    setProductError(productOk ? "" : "Choose the product before approving.");
    const approving = decision === "approve" && showInvoiceDate;
    const invoiceDateOk = !approving || checkInvoiceDate(invoiceDate, invoiceDateRequired);
    const reasonOk = !(approving && reasonNeeded) || checkInvoiceReason(invoiceReason);
    if (decision === "reject") {
      setInvoiceDateError("");
      setInvoiceReasonError("");
    }
    if (!noteOk || !dateOk || !productOk || !invoiceDateOk || !reasonOk) {
      return;
    }
    setChoice(decision);
    setStage("confirm");
  };

  const submit = async () => {
    if (inFlight || choice === null) {
      return;
    }
    setInFlight(true);
    setFailure(null);
    let result;
    if (isClaim) {
      // The existing claim view takes exactly decision, claimant_account_id and note.
      result = await reviewClaim(
        accessToken,
        item.registrationId,
        choice,
        item.claimantAccountId,
        note.trim(),
        invoiceDateBody(choice)
      );
    } else {
      const body = buildBody(choice);
      result = await reviewRegistration(
        accessToken,
        item.registrationId,
        body,
        requestIdFor(idRef, `review:${item.registrationId}:${JSON.stringify(body)}`)
      );
      settleRequestId(idRef, result);
    }
    if (!mounted.current) {
      return;
    }
    setInFlight(false);
    if (result.ok) {
      onDecided(item);
    } else {
      const reported = result.details?.purchaseDateSource;
      if (result.code === "DATE_DECISION_REQUIRED") {
        setSourceOverride("customer_claim");
      } else if (typeof reported === "string") {
        // The OMS says where the date stands now. With no source in the answer nothing is assumed:
        // the person reloads, and the queue item or detail read stays the reference.
        if (result.code === "REVIEW_DATE_CHANGED" || result.code === "DATE_DECISION_NOT_APPLICABLE") {
          setSourceOverride(reported);
        }
      } else if (result.code === "DATE_DECISION_NOT_APPLICABLE") {
        // This code means the date does not need a decision; the source itself is not known.
        setSourceOverride("");
      }
      if (result.code === "PRODUCT_REQUIRED" || result.code === "PRODUCT_NOT_FOUND") {
        setProductRefused(true);
      }
      // The invoice date's refusals go under its field (an approve that sent one, for DATE_INVALID).
      const invoiceDateSent = choice === "approve" && invoiceDate.trim() !== "";
      if (result.code === "PURCHASE_DATE_REQUIRED") {
        setDateMissingOverride(true);
        setInvoiceDateError(result.message);
      } else if (result.code === "DATE_INVALID" && invoiceDateSent && showInvoiceDate) {
        setInvoiceDateError(result.message);
      } else if (result.code === "PURCHASE_DATE_REASON_REQUIRED") {
        setReasonOverride(true);
        setInvoiceReasonError(result.message);
      } else if (
        result.code === "REVIEW_DATE_CHANGED" &&
        TRUSTED_DATE_SOURCES.has(result.details?.purchaseDateSource)
      ) {
        // The row now has a trusted date: a date entered here replaces it, so it needs a reason.
        setReasonOverride(true);
      }
      setFailure({ message: result.message, code: result.code, details: result.details || {} });
      setStage("edit");
    }
  };

  const reloadThis = () => {
    setFailure(null);
    setStage("edit");
    setChoice(null);
    setDateDecision("leave_unverified");
    setCorrectDate("");
    setDateError("");
    setInvoiceDate("");
    setInvoiceDateError("");
    setInvoiceReason("");
    setInvoiceReasonError("");
    setDateMissingOverride(false);
    setReasonOverride(false);
    setProductId("");
    setProductError("");
    setProductRefused(false);
    loadDetail();
  };

  // A refusal already shown under the invoice date field (or its reason) is not repeated above.
  const fieldShowsFailure = (code) =>
    showInvoiceDate &&
    (code === "PURCHASE_DATE_REASON_REQUIRED" ||
      (INVOICE_DATE_CODES.has(code) && invoiceDateError !== ""));

  const canDecide = !notPending && detail.status !== "loading";
  const claimMissing = isClaim && !item.claimantAccountId;

  return (
    <Modal
      show
      onHide={inFlight ? () => {} : onClose}
      backdrop={inFlight ? "static" : true}
      keyboard={!inFlight}
      className="commonModal"
      dialogClassName="warranty-publish-dialog"
      scrollable
      centered
    >
      <Modal.Header closeButton={!inFlight}>
        <Modal.Title>
          {isClaim ? "Ownership claim" : "Review registration"}: {frame}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="warranty-readonly">
          <dl>
            <dt>Why it is here</dt>
            <dd>{reviewReasonLabel(item.reason)}</dd>
            <dt>Phone</dt>
            <dd>{item.maskedPhone || "-"}</dd>
            {item.sale?.productName && (
              <>
                <dt>Product</dt>
                <dd>{item.sale.productName}</dd>
              </>
            )}
            {item.sale?.dealerName && (
              <>
                <dt>Dealer</dt>
                <dd>
                  {item.sale.dealerName}
                  {item.sale.channel ? `, ${item.sale.channel}` : ""}
                </dd>
              </>
            )}
            {isClaim && (
              <>
                <dt>Claimant account</dt>
                <dd>{item.claimantAccountId || "-"}</dd>
                <dt>Purchase date</dt>
                <dd>
                  {hasDate ? formatDate(purchaseDate) : "None"}
                  {hasDate && source ? (
                    <span className="warranty-muted"> {dateSourceLabel(source)}</span>
                  ) : null}
                </dd>
              </>
            )}
            <dt>Created</dt>
            <dd>{formatDateTime(item.createdAt)}</dd>
            {ready && (
              <>
                <dt>Status</dt>
                <dd>{registrationStatusLabel(ready.status)}</dd>
              </>
            )}
          </dl>
        </div>

        {detail.status === "loading" && <Loading label="Loading the review details" />}
        {detail.status === "fallback" && !isClaim && detail.switchedOff && (
          <p className="warranty-muted warranty-ocr-note">OCR details are not switched on yet.</p>
        )}
        {detail.status === "fallback" && !isClaim && !detail.switchedOff && (
          <div className="warranty-banner is-warning" role="alert">
            <span>The OCR details could not be loaded: {detail.message}</span>
            <div className="warranty-banner-actions">
              <button type="button" className="warranty-inline-btn" onClick={loadDetail}>
                Try again
              </button>
            </div>
          </div>
        )}

        {!isClaim && detail.status !== "loading" && (
          <div className="warranty-date-compare" aria-label="Purchase date">
            <div>
              <h6>Purchase date on the registration</h6>
              <div className="warranty-date-value">{formatDate(purchaseDate)}</div>
              <div className="warranty-muted">{source ? dateSourceLabel(source) : "No source"}</div>
            </div>
            {ready &&
              ready.documents.map((doc, index) => (
                <div key={doc.id}>
                  <h6>Read from document {index + 1}</h6>
                  <div className="warranty-date-value">
                    {doc.ocrDate ? formatDate(doc.ocrDate) : "No date read"}
                  </div>
                  <div className="warranty-muted">
                    {percent(doc.ocrConfidence)}
                    {doc.ocrFrameNumber &&
                      `${percent(doc.ocrConfidence) ? ", " : ""}frame ${doc.ocrFrameNumber} ${
                        doc.ocrFrameMatches === true
                          ? "matches"
                          : doc.ocrFrameMatches === false
                          ? "does not match"
                          : "(not compared)"
                      }`}
                  </div>
                  {doc.ocrRead && (
                    <dl className="warranty-ocr-read" aria-label={`Read from document ${index + 1}`}>
                      {[
                        ["Customer", doc.ocrRead.customerName],
                        ["Invoice no.", doc.ocrRead.invoiceNumber],
                        [
                          "Total",
                          doc.ocrRead.totalMinor === null
                            ? null
                            : formatMinor(doc.ocrRead.totalMinor, doc.ocrRead.currency || "INR"),
                        ],
                        ["Dealer", doc.ocrRead.dealerName],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <dt>{label}</dt>
                          <dd>{value || "Not read"}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              ))}
          </div>
        )}

        {ready?.invoiceCheck && (
          <div className="warranty-readonly">
            <h6>Invoice check: {invoiceCheckStateLabel(ready.invoiceCheck.state)}</h6>
            {ready.invoiceCheck.failures.length > 0 && (
              <ul>
                {ready.invoiceCheck.failures.map((code) => (
                  <li key={code}>{invoiceFailureLabel(code)}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {detail.status !== "loading" && (
          <div className="warranty-readonly">
            <h6>Documents</h6>
            {documents.length === 0 && (
              <div className="warranty-muted">No document was uploaded for this registration.</div>
            )}
            {documents.map((doc, index) => (
              <DocumentPreview
                key={doc.id}
                doc={doc}
                index={index}
                link={links[doc.id]}
                onRefresh={() => fetchLink(doc.id)}
                onBroken={() =>
                  setLinks((prev) => ({ ...prev, [doc.id]: { ...prev[doc.id], broken: true } }))
                }
              />
            ))}
          </div>
        )}

        {notPending && (
          <div className="warranty-banner is-info" role="status">
            <span>
              This registration is no longer waiting for review (status:{" "}
              {registrationStatusLabel(ready.status)}).
            </span>
            <div className="warranty-banner-actions">
              <button type="button" className="warranty-inline-btn" onClick={onStale}>
                Close and reload the queue
              </button>
            </div>
          </div>
        )}
        {claimMissing && (
          <div className="warranty-banner is-error" role="alert">
            <span>This claim has no claimant account, so it cannot be decided from here.</span>
          </div>
        )}

        {canDecide && !claimMissing && stage === "edit" && (
          <div className="formloginset warranty-decision">
            {showInvoiceDate && (
              <fieldset className="warranty-date-decision">
                <legend>Purchase date (from the invoice)</legend>
                <Form.Group controlId="warrantyInvoiceDate">
                  <Form.Label>
                    Date on the invoice{invoiceDateRequired ? "" : " (optional)"}
                  </Form.Label>
                  <p className="warranty-help" id="warrantyInvoiceDateHint">
                    {invoiceDateRequired
                      ? "This registration has no purchase date. Read it off the invoice above: the warranty and plans start from this date. Needed to approve."
                      : hasDate && TRUSTED_DATE_SOURCES.has(source)
                      ? `The registration already has a verified date (${formatDate(
                          purchaseDate
                        )}). Fill this only if the invoice shows a different one, and say why.`
                      : "Fill this only if the invoice shows a different date. It replaces the date on the registration."}
                  </p>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="YYYY-MM-DD"
                    maxLength={10}
                    value={invoiceDate}
                    aria-describedby="warrantyInvoiceDateHint"
                    aria-required={invoiceDateRequired}
                    aria-invalid={invoiceDateError !== ""}
                    onChange={(event) => {
                      setInvoiceDate(event.target.value);
                      if (invoiceDateError) {
                        checkInvoiceDate(event.target.value, false);
                      }
                    }}
                    onBlur={() => checkInvoiceDate(invoiceDate, false)}
                    disabled={inFlight}
                    autoComplete="off"
                  />
                  {invoiceDateError && (
                    <div className="danger-colorset" role="alert">
                      {invoiceDateError}
                    </div>
                  )}
                </Form.Group>
                {reasonNeeded && (
                  <Form.Group className="mt-2" controlId="warrantyInvoiceDateReason">
                    <Form.Label>Why is the verified date wrong? (kept in the audit trail)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      maxLength={REASON_MAX}
                      value={invoiceReason}
                      aria-invalid={invoiceReasonError !== ""}
                      onChange={(event) => {
                        setInvoiceReason(event.target.value);
                        if (invoiceReasonError) {
                          checkInvoiceReason(event.target.value);
                        }
                      }}
                      disabled={inFlight}
                    />
                    {invoiceReasonError && (
                      <div className="danger-colorset" role="alert">
                        {invoiceReasonError}
                      </div>
                    )}
                  </Form.Group>
                )}
              </fieldset>
            )}
            {dateRequired && (
              <fieldset className="warranty-date-decision">
                <legend>Purchase date decision (applies when you approve)</legend>
                <p className="warranty-help">
                  The rider typed this date; nobody has checked it. Choose what to do with it.
                </p>
                {DATE_DECISIONS.map((option) => (
                  <Form.Check
                    key={option.value}
                    type="radio"
                    id={`warranty-date-decision-${option.value}`}
                    name="warrantyDateDecision"
                    label={
                      <>
                        <strong>{option.label}</strong>
                        <span className="warranty-muted"> {option.help}</span>
                      </>
                    }
                    checked={dateDecision === option.value}
                    onChange={() => {
                      setDateDecision(option.value);
                      setDateError("");
                    }}
                    disabled={inFlight}
                  />
                ))}
                {dateDecision === "correct" && (
                  <Form.Group className="mt-2" controlId="warrantyCorrectDate">
                    <Form.Label>Correct purchase date</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      placeholder="YYYY-MM-DD"
                      maxLength={10}
                      value={correctDate}
                      onChange={(event) => {
                        setCorrectDate(event.target.value);
                        checkDate(event.target.value);
                      }}
                      onBlur={() => checkDate(correctDate)}
                      disabled={inFlight}
                      autoComplete="off"
                    />
                    {dateError && (
                      <div className="danger-colorset" role="alert">
                        {dateError}
                      </div>
                    )}
                  </Form.Group>
                )}
              </fieldset>
            )}
            {needsProduct && (
              <>
                <ProductPicker
                  options={products.options}
                  state={products.state}
                  value={productId}
                  onChange={(value) => {
                    setProductId(value);
                    setProductError("");
                  }}
                  disabled={inFlight}
                  required={productRequired}
                />
                {productError && (
                  <div className="danger-colorset" role="alert">
                    {productError}
                  </div>
                )}
              </>
            )}
            <Form.Group className="mt-2" controlId="warrantyReviewNote">
              <Form.Label>Note (kept in the audit trail)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={500}
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                  if (noteError) {
                    checkNote(event.target.value, false);
                  }
                }}
                placeholder="Optional for an approval, needed for a rejection"
                disabled={inFlight}
              />
              {noteError && (
                <div className="danger-colorset" role="alert">
                  {noteError}
                </div>
              )}
            </Form.Group>
          </div>
        )}

        {stage === "confirm" && (
          <div className="warranty-confirm" role="group" aria-label="Confirm the decision">
            <strong>
              {choice === "approve" ? "Approve" : "Reject"} {isClaim ? "this claim" : "this registration"}?
            </strong>
            <p>
              {effectText({
                isClaim,
                choice,
                dateDecision,
                correctDate: correctDate.trim(),
                purchaseDate,
                dateRequired,
                frame,
                invoiceDate: choice === "approve" && showInvoiceDate ? invoiceDate.trim() : "",
              })}
            </p>
            {choice === "approve" && needsProduct && productId !== "" && (
              <p className="warranty-muted">
                Product: {products.options.find((option) => option.value === productId)?.label || productId}
              </p>
            )}
            {note.trim() !== "" && <p className="warranty-muted">Note: {note.trim()}</p>}
          </div>
        )}

        {failure && !(stage === "edit" && fieldShowsFailure(failure.code)) && (
          <div className="warranty-dialog-error" role="alert">
            {failure.message}
            {GONE_CODES.has(failure.code) && (
              <>
                {" "}
                <button type="button" className="warranty-inline-btn" onClick={onStale}>
                  Close and reload the queue
                </button>
              </>
            )}
            {MOVED_CODES.has(failure.code) && (
              <>
                {" "}
                {detail.status === "ready" ? (
                  <button type="button" className="warranty-inline-btn" onClick={reloadThis}>
                    Reload this registration
                  </button>
                ) : (
                  // Without the review detail the queue item is the only copy: read the queue again.
                  <button type="button" className="warranty-inline-btn" onClick={onStale}>
                    Close and reload the queue
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {stage === "confirm" ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setStage("edit");
                setChoice(null);
              }}
              disabled={inFlight}
            >
              Back
            </Button>
            <Button className="addbtncmn" onClick={submit} disabled={inFlight}>
              {inFlight
                ? "Working"
                : `${choice === "approve" ? "Approve" : "Reject"} ${isClaim ? "claim" : "registration"}`}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={inFlight}>
              Close
            </Button>
            {canDecide && !claimMissing && (
              <>
                <Button
                  variant="outline-danger"
                  onClick={() => goToConfirm("reject")}
                  disabled={inFlight}
                >
                  {isClaim ? "Reject claim" : "Reject"}
                </Button>
                <Button
                  className="addbtncmn"
                  onClick={() => goToConfirm("approve")}
                  disabled={inFlight || productBlocked}
                >
                  {isClaim ? "Approve claim" : "Approve"}
                </Button>
              </>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ReviewDecision;
